import { showNotification } from '@mantine/notifications';
import { IconX } from '@tabler/icons-react';
import React, { useEffect, useMemo } from 'react';

import { useAuth } from '../contexts/AuthProvider';
import { useI18n } from '../contexts/I18nProvider';
import { BuiltInPermissions, ScopeEnum } from '../types/api/response/role';
import { hasPermission } from '../utils/authorization';
import useCompany from './useCompany';
import useFetchUsers from './useFetchUsers';

export default function useFetchManagers() {
  const { user } = useAuth();
  const { t } = useI18n();
  const { id: companyId } = useCompany(user);
  /*
   * Passe par useFetchUsers, seule source qui résout employee.role : hasPermission exige
   * role.permissions, absent de la liste brute des salariés (la route consolidée exclut les champs
   * relationnels pour éviter un N+1). Sans ça le filtre rejette tous les candidats et la liste des
   * validateurs est vide.
   */
  const { users, isLoading, isError } = useFetchUsers(companyId);

  const managers = useMemo(() => {
    const employeesFilter = users.filter(
      (employee) =>
        Boolean(employee.valid) &&
        hasPermission(
          [
            {
              permission: BuiltInPermissions.ConfigureEmployees,
              scope: ScopeEnum.ALL,
            },
            {
              permission: BuiltInPermissions.ValidateLeavesAbsences,
              scope: ScopeEnum.ALL,
            },
          ],
          employee,
          companyId
        )
    );
    employeesFilter.unshift({ id: '', mail: t('w.noValue') });

    return employeesFilter.map((employee) => {
      return {
        value: employee.id,
        label: employee.fullname
          ? `${employee.fullname} (${employee.mail})`
          : employee.mail!,
      };
    });
  }, [users, companyId]);

  useEffect(() => {
    if (isError) {
      showNotification({
        id: 'error-response',
        title: t('w.error'),
        message: t('error.error_happened'),
        color: 'red',
        icon: <IconX />,
      });
    }
  }, [isError]);

  return { managers, loading: isLoading };
}
